/**
 * The live preview inside the phone and the browser window.
 *
 * Both frames load the tracker's own preview document (web/preview.html): the
 * real app, drawn from a sample ledger, sealed so that it sends no request and
 * reads no session. The drawings in preview.js stay underneath as the picture
 * shown until the app is ready -- and the one that stays if it never is.
 *
 * Only a preview on this page's own origin is embedded. The app sends
 * `frame-ancestors 'self'` for that one document, so a different origin could
 * not frame it anyway; checking first keeps the drawing instead of an empty,
 * refused frame. Being same-origin is also what lets the page steer it.
 */

/** The size each layout is drawn at before it is scaled into its frame. */
const LAYOUTS = {
  mobile: { width: 390, height: 876 },
  desktop: { width: 1280, height: 800 }
};

function previewAddress(appUrl) {
  try {
    const url = new URL('preview.html', new URL(appUrl, window.location.href));
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
}

/**
 * @returns {{ show(layout, tab), teardown() }}
 */
export function livePreviews(root, { appUrl, lang, title }) {
  const address = previewAddress(appUrl);
  const slots = [...root.querySelectorAll('[data-live]')];
  const frames = new Map();
  const noop = { show() {}, teardown() {} };
  if (!address || !slots.length) return noop;

  for (const slot of slots) {
    const layout = slot.getAttribute('data-live') === 'mobile' ? 'mobile' : 'desktop';
    const { width, height } = LAYOUTS[layout];
    const src = new URL(address);
    src.search = new URLSearchParams({ layout, lang, tab: slot.getAttribute('data-tab') || 'overview' }).toString();

    const frame = document.createElement('iframe');
    frame.className = 'live__frame';
    frame.title = title;
    frame.width = String(width);
    frame.height = String(height);
    frame.loading = 'lazy';
    /*
     * A picture of the app, not a second app on the page: out of the tab
     * order, silent to screen readers (the section already says what it
     * shows), and no pointer input -- a scroll over it moves this page.
     */
    frame.tabIndex = -1;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('inert', '');
    frame.style.width = width + 'px';
    frame.style.height = height + 'px';
    frame.src = src.toString();
    slot.appendChild(frame);
    frames.set(frame, { slot, layout, width, height, queued: null });
  }

  /*
   * Scaled to the width it is given. The slots keep the layout's proportions
   * in CSS (the phone's screen, the window's 16:10 stage), so the height
   * follows and nothing on the page moves when the app takes over.
   */
  const fit = () => {
    for (const [frame, entry] of frames) {
      const scale = entry.slot.clientWidth / entry.width;
      if (scale) frame.style.transform = `scale(${scale})`;
    }
  };
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
  slots.forEach(slot => resize?.observe(slot));
  window.addEventListener('resize', fit);
  fit();

  const send = (frame, tab) => {
    frame.contentWindow?.postMessage({ pt: 'preview-tab', tab }, window.location.origin);
  };

  const onMessage = event => {
    if (event.origin !== window.location.origin || event.data?.pt !== 'preview') return;
    for (const [frame, entry] of frames) {
      if (frame.contentWindow !== event.source) continue;
      if (event.data.ready) {
        entry.slot.closest('[data-live-host]')?.classList.add('is-live');
        entry.ready = true;
        if (entry.queued) send(frame, entry.queued);
      }
    }
  };
  window.addEventListener('message', onMessage);

  return {
    /** Switches the screen shown in every frame of that layout. */
    show(layout, tab) {
      for (const [frame, entry] of frames) {
        if (entry.layout !== layout) continue;
        entry.queued = tab;
        if (entry.ready) send(frame, tab);
      }
    },
    teardown() {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('resize', fit);
      resize?.disconnect();
    }
  };
}
